import { MemCacheService } from '@csp/csp-common-memcache';
import {
  ApiOptions,
  CallbackWith,
  OrgId,
  PagedResult,
  Patient,
  PatientPauseStatusType,
  StateAssert,
  TenantId,
  UserId,
} from '@csp/csp-common-model';
import {
  RequestWhileCriterion,
  ScheduleRequestInfoMapper,
  ScheduleVersionRequestRef,
  findScheduleVersionsActiveNow,
} from '@csp/csp-common-scheduling';
import { toRestOptions } from '@csp/csp-fe-auth';
import { DeviceObservationsRestServiceV1, DeviceSchedulesRestServiceV1 } from '@csp/dmdp-api-client';
import { PagedResultMapper } from '@csp/csp-common-mapper';
import { PagedResultUtil, isDefined } from '@csp/csp-common-util';
import { Page, largePage } from '@csp/dmdp-api-common-dto';
import { DeviceObservationIncludeFieldV1, DeviceObservationQueryOperatorV1 } from '@csp/dmdp-api-device-dto';
import { ScheduleReferenceDateService } from '@csp/csp-common-patient-scheduling';
import { hoursToSeconds } from 'date-fns';
import { DeviceObservationRequest } from '../model/DeviceObservationRequest';
import { DeviceObservationRequestInfo } from '../model/DeviceObservationRequestInfo';
import { DeviceObservationScheduleRequestInfo } from '../model/DeviceObservationScheduleRequestInfo';
import { DeviceRequestUtil } from '../util/DeviceRequestUtil';
import { DeviceObservation } from '../model/DeviceObservation';
import { DeviceObservationMapper } from '../mapper/DeviceObservationMapper';
import { DeviceObservationScheduleService } from './DeviceObservationScheduleService';
import { DeviceObservationQuery } from './DeviceObservationQuery';

const DEVICE_OBSERVATION_CACHE_KEY = 'device.request.';

const generateRequestCacheKey = (ref: ScheduleVersionRequestRef, tenantId?: TenantId): string[] => {
  if (tenantId) {
    return [DEVICE_OBSERVATION_CACHE_KEY, ref.scheduleCode, ref.versionCode, ref.requestCode, tenantId];
  }
  return [DEVICE_OBSERVATION_CACHE_KEY, ref.scheduleCode, ref.versionCode, ref.requestCode];
};

const getActiveRequests = async (
  { firstOrgId }: Patient,
  apiOptions?: ApiOptions,
): Promise<DeviceObservationRequest[]> => {
  StateAssert.notNull(firstOrgId, 'Patient firstOrgId not set in getActiveRequests');

  const activeSchedules = await DeviceObservationScheduleService.getCachedActiveSchedules(firstOrgId, apiOptions);
  return findScheduleVersionsActiveNow(activeSchedules).flatMap(v => v.requests);
};

const getAllRequestsByOrgId = async (orgId: OrgId, apiOptions?: ApiOptions): Promise<DeviceObservationRequest[]> => {
  const requests = await DeviceObservationScheduleService.getCachedActivatedDeviceSchedulesForOrganizationIds(
    [orgId],
    apiOptions,
  );

  return requests.getRequestsByOrgId(orgId);
};

const getAllRequestsByOrgIds = async (
  orgIds: OrgId[],
  apiOptions?: ApiOptions,
): Promise<DeviceObservationRequest[]> => {
  const requests = await DeviceObservationScheduleService.getCachedActivatedDeviceSchedulesForOrganizationIds(
    orgIds,
    apiOptions,
  );

  return requests.getAllRequests();
};

const getAllRequests = async (
  { firstOrgId }: Patient,
  apiOptions?: ApiOptions,
): Promise<DeviceObservationRequest[]> => {
  StateAssert.notNull(firstOrgId, 'Patient firstOrgId not set in getAllRequests');

  return getAllRequestsByOrgId(firstOrgId, apiOptions);
};

const getAllRequestInfos = async (
  patient: Patient,
  apiOptions?: ApiOptions,
  errorHandler?: CallbackWith<unknown>,
): Promise<DeviceObservationScheduleRequestInfo[]> => {
  StateAssert.notNull(patient.firstOrgId, 'Patient firstOrgId not set in getAllRequestInfos');

  const orgSchedules = await DeviceObservationScheduleService.getCachedActivatedDeviceSchedulesForOrganizationIds(
    [patient.firstOrgId],
    apiOptions,
    errorHandler,
  );

  const scheduleRequests: DeviceObservationScheduleRequestInfo[] = ScheduleRequestInfoMapper.from(
    orgSchedules.getAllSchedules(),
    ScheduleReferenceDateService.fromPatientExcludingStatusTypes(patient, Object.values(PatientPauseStatusType)),
  );

  return scheduleRequests;
};

const getActiveRequestsWithInfo = async (
  patient: Patient,
  apiOptions?: ApiOptions,
): Promise<DeviceObservationScheduleRequestInfo[]> => {
  StateAssert.notNull(patient.firstOrgId, 'Patient firstOrgId not set in getActiveRequestsWithInfo');

  const orgSchedules = await DeviceObservationScheduleService.getCachedActivatedDeviceSchedulesForOrganizationIds(
    [patient.firstOrgId],
    apiOptions,
  );

  const scheduleRequests: DeviceObservationScheduleRequestInfo[] = ScheduleRequestInfoMapper.from(
    orgSchedules.getAllSchedules(),
    ScheduleReferenceDateService.fromPatientExcludingStatusTypes(patient, Object.values(PatientPauseStatusType)),
  );

  return DeviceRequestUtil.filterOnlyVisibleStatuses(scheduleRequests);
};

const getDeviceObservationRequestInfoByScheduleVersionRequestRef = async (
  { scheduleCode, versionCode, requestCode }: ScheduleVersionRequestRef,
  apiOptions?: ApiOptions,
): Promise<DeviceObservationRequestInfo> => {
  const scheduleVersionRequest = await DeviceSchedulesRestServiceV1.getObservationScheduleVersionRequest(
    toRestOptions(apiOptions),
    scheduleCode,
    versionCode,
    requestCode,
  );

  return DeviceObservationRequestInfo.from(
    scheduleVersionRequest.scheduleCode,
    scheduleVersionRequest.version.versionCode,
    scheduleVersionRequest.version.request,
  );
};

const getCachedDeviceObservationRequestInfoByScheduleVersionRequestRef = async (
  scheduleRequestRef: ScheduleVersionRequestRef,
  tenantId?: TenantId,
  apiOptions?: ApiOptions,
): Promise<DeviceObservationRequestInfo> => {
  let observationRequestInfo = MemCacheService.getValue<DeviceObservationRequestInfo>(
    generateRequestCacheKey(scheduleRequestRef, tenantId),
  );

  if (!observationRequestInfo) {
    observationRequestInfo = await getDeviceObservationRequestInfoByScheduleVersionRequestRef(
      scheduleRequestRef,
      apiOptions,
    );

    MemCacheService.setValue(
      generateRequestCacheKey(scheduleRequestRef, tenantId),
      hoursToSeconds(1),
      observationRequestInfo,
    );
  }

  return observationRequestInfo;
};

const getActiveAndFutureRequestInfosForTenant = async (
  patient: Patient,
  whileCriteria?: RequestWhileCriterion[],
  apiOptions?: ApiOptions,
): Promise<DeviceObservationScheduleRequestInfo[]> => {
  const requests = await DeviceObservationRequestService.getAllRequestInfos(patient, apiOptions);
  const filteredRequests = whileCriteria
    ? requests.filter(requestInfo => requestInfo.hasMatchingRequestWhileCriteria(whileCriteria))
    : requests;
  return filteredRequests.filter(DeviceRequestUtil.isNotExpired);
};

const fetchUsersWithValidNonConsentData = async (
  userIds?: UserId[],
  page?: Page,
  apiOptions?: ApiOptions,
): Promise<PagedResult<DeviceObservation>> => {
  const { observations, paging } = await DeviceObservationsRestServiceV1.queryDeviceObservations(
    toRestOptions(apiOptions),
    {
      fields: [
        DeviceObservationIncludeFieldV1.USER_ID,
        DeviceObservationIncludeFieldV1.SESSION_CODE,
        DeviceObservationIncludeFieldV1.DEVICE_OBSERVATION_ID,
        DeviceObservationIncludeFieldV1.SYNCHRONIZATION_TIMESTAMP,
      ],
      criterion: {
        operator: DeviceObservationQueryOperatorV1.AND,
        criteria: [
          userIds?.length ? DeviceObservationQuery.byUserIds(userIds) : undefined,
          DeviceObservationQuery.byInvalidated(false),
          DeviceObservationQuery.byNonConsentData(true),
        ].filter(isDefined),
      },
    },
    page ?? largePage(),
  );
  return PagedResultMapper.toPagedResult(paging, observations?.map(DeviceObservationMapper.from) ?? []);
};

const fetchAllUsersWithValidNonConsentData = async (
  userIds?: UserId[],
  apiOptions?: ApiOptions,
): Promise<DeviceObservation[]> => {
  const getPage = (page: Page): Promise<PagedResult<DeviceObservation>> =>
    fetchUsersWithValidNonConsentData(userIds, page, apiOptions);
  return PagedResultUtil.getAllItems(getPage, 'data');
};

export const DeviceObservationRequestService = {
  getActiveRequests,
  getAllRequests,
  getAllRequestInfos,
  getAllRequestsByOrgId,
  getAllRequestsByOrgIds,
  getActiveRequestsWithInfo,
  getActiveAndFutureRequestInfosForTenant,
  getDeviceObservationRequestInfoByScheduleVersionRequestRef,
  getCachedDeviceObservationRequestInfoByScheduleVersionRequestRef,
  fetchAllUsersWithValidNonConsentData,
};
